/**
 * Accessibility utilities for Partner Admin Panel
 * Provides helpers for ARIA attributes, keyboard navigation, and focus management
 */

// Focus trap for modals and dialogs
export function trapFocus(element: HTMLElement) {
  const focusableElements = element.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Tab') return

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement?.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement?.focus()
        e.preventDefault()
      }
    }
  }

  element.addEventListener('keydown', handleKeyDown)
  firstElement?.focus()

  return () => {
    element.removeEventListener('keydown', handleKeyDown)
  }
}

// Announce message to screen readers
export function announce(message: string, priority: 'polite' | 'assertive' = 'polite') {
  const announcement = document.createElement('div')
  announcement.setAttribute('role', 'status')
  announcement.setAttribute('aria-live', priority)
  announcement.setAttribute('aria-atomic', 'true')
  announcement.className = 'sr-only'
  announcement.textContent = message
  document.body.appendChild(announcement)

  setTimeout(() => {
    document.body.removeChild(announcement)
  }, 1000)
}

// Generate unique IDs for ARIA relationships
let idCounter = 0
export function generateId(prefix: string = 'aria'): string {
  return `${prefix}-${++idCounter}`
}

// Keyboard navigation helpers
export const KeyCodes = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End',
} as const

// Handle keyboard navigation for lists and menus
export function handleListNavigation(
  e: React.KeyboardEvent,
  items: HTMLElement[],
  currentIndex: number,
  onSelect: (index: number) => void,
  orientation: 'horizontal' | 'vertical' = 'vertical'
) {
  const nextKey = orientation === 'vertical' ? KeyCodes.ARROW_DOWN : KeyCodes.ARROW_RIGHT
  const prevKey = orientation === 'vertical' ? KeyCodes.ARROW_UP : KeyCodes.ARROW_LEFT

  switch (e.key) {
    case nextKey:
      e.preventDefault()
      const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
      items[nextIndex]?.focus()
      onSelect(nextIndex)
      break
    case prevKey:
      e.preventDefault()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
      items[prevIndex]?.focus()
      onSelect(prevIndex)
      break
    case KeyCodes.HOME:
      e.preventDefault()
      items[0]?.focus()
      onSelect(0)
      break
    case KeyCodes.END:
      e.preventDefault()
      items[items.length - 1]?.focus()
      onSelect(items.length - 1)
      break
    case KeyCodes.ENTER:
    case KeyCodes.SPACE:
      e.preventDefault()
      // Trigger click on current item
      items[currentIndex]?.click()
      break
  }
}

// ARIA label helpers
export const ariaLabels = {
  // Navigation
  mainNavigation: 'Navegação principal',
  sidebarToggle: 'Alternar barra lateral',
  closeMenu: 'Fechar menu',
  openMenu: 'Abrir menu',

  // Dashboard
  dashboardStats: 'Estatísticas do dashboard',
  recentActivities: 'Atividades recentes',
  systemStatus: 'Status do sistema',

  // Tables
  sortAscending: (column: string) => `Ordenar ${column} em ordem crescente`,
  sortDescending: (column: string) => `Ordenar ${column} em ordem decrescente`,
  selectRow: (item: string) => `Selecionar ${item}`,
  selectAllRows: 'Selecionar todas as linhas',

  // Actions
  edit: (item: string) => `Editar ${item}`,
  delete: (item: string) => `Excluir ${item}`,
  view: (item: string) => `Visualizar ${item}`,
  add: (item: string) => `Adicionar ${item}`,
  refresh: 'Atualizar dados',
  search: 'Pesquisar',
  filter: 'Filtrar resultados',

  // Forms
  required: 'Campo obrigatório',
  optional: 'Campo opcional',
  showPassword: 'Mostrar senha',
  hidePassword: 'Ocultar senha',

  // Modals
  closeModal: 'Fechar modal',
  confirmAction: 'Confirmar ação',
  cancelAction: 'Cancelar ação',

  // Status
  loading: 'Carregando...',
  success: 'Operação realizada com sucesso',
  error: 'Erro na operação',

  // Pagination
  previousPage: 'Página anterior',
  nextPage: 'Próxima página',
  goToPage: (page: number) => `Ir para página ${page}`,
  currentPage: (page: number, total: number) => `Página ${page} de ${total}`,
}

// Skip link component styles (to be used in CSS)
export const skipLinkStyles = `
  .skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: #1f2937;
    color: #ffffff;
    padding: 8px 16px;
    z-index: 100;
    text-decoration: none;
    font-weight: 500;
    border-radius: 0 0 8px 0;
    transition: top 0.2s ease;
  }

  .skip-link:focus {
    top: 0;
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`

// Screen reader only class (hide visually but keep accessible)
export const srOnlyStyles = `
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .sr-only-focusable:focus {
    position: static;
    width: auto;
    height: auto;
    padding: inherit;
    margin: inherit;
    overflow: visible;
    clip: auto;
    white-space: normal;
  }
`

// Focus visible styles for better keyboard navigation
export const focusVisibleStyles = `
  :focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  :focus:not(:focus-visible) {
    outline: none;
  }

  button:focus-visible,
  a:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 4px;
  }
`

// High contrast mode detection
export function prefersHighContrast(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-contrast: more)').matches
}

// Reduced motion detection
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Color contrast checker (WCAG AA requires 4.5:1 for normal text, 3:1 for large text)
export function getContrastRatio(foreground: string, background: string): number {
  const getLuminance = (color: string) => {
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16) / 255
    const g = parseInt(hex.substr(2, 2), 16) / 255
    const b = parseInt(hex.substr(4, 2), 16) / 255

    const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)

    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
  }

  const l1 = getLuminance(foreground)
  const l2 = getLuminance(background)
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)

  return (lighter + 0.05) / (darker + 0.05)
}

export function meetsContrastRequirement(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  isLargeText: boolean = false
): boolean {
  const ratio = getContrastRatio(foreground, background)

  if (level === 'AAA') {
    return isLargeText ? ratio >= 4.5 : ratio >= 7
  }
  return isLargeText ? ratio >= 3 : ratio >= 4.5
}
